<script lang="ts">
  import { createEventDispatcher } from 'svelte';
  
  const dispatch = createEventDispatcher<{ win: number }>();
  const dispatchLoss = createEventDispatcher<void>();
  
  let { onBack, playerName = 'Player' } = $props();
  
  let playerHand = $state<string[]>([]);
  let dealerHand = $state<string[]>([]);
  let deck = $state<string[]>([]);
  let gameMessage = $state('Place your bet and deal!');
  let gameOver = $state(false);
  let difficulty = $state(1);
  
  const suits = ['♠', '♥', '♦', '♣'];
  const values = ['2', '3', '4', '5', '6', '7', '8', '9', '10', 'J', 'Q', 'K', 'A'];
  
  function createDeck() {
    deck = [];
    for (const suit of suits) {
      for (const value of values) {
        deck.push(`${value}${suit}`);
      }
    }
    // Shuffle
    deck = deck.sort(() => Math.random() - 0.5);
  }
  
  function getCardValue(card: string): number {
    const value = card.slice(0, -1);
    if (['J', 'Q', 'K'].includes(value)) return 10;
    if (value === 'A') return 11;
    return parseInt(value);
  }
  
  function calculateHand(hand: string[]): number {
    let total = 0;
    let aces = 0;
    
    for (const card of hand) {
      total += getCardValue(card);
      if (card.startsWith('A')) aces++;
    }
    
    while (total > 21 && aces > 0) {
      total -= 10;
      aces--;
    }
    
    return total;
  }
  
  function deal() {
    createDeck();
    playerHand = [deck.pop()!, deck.pop()!];
    dealerHand = [deck.pop()!];
    gameMessage = 'Hit or Stand?';
    gameOver = false;
    
    // Check for blackjack
    if (calculateHand(playerHand) === 21) {
      handleWin(true);
    }
  }
  
  function hit() {
    if (gameOver) return;
    
    playerHand.push(deck.pop()!);
    const playerTotal = calculateHand(playerHand);
    
    if (playerTotal > 21) {
      gameMessage = 'Bust! You went over 21.';
      gameOver = true;
      handleLoss();
    } else if (playerTotal === 21) {
      stand();
    } else {
      gameMessage = `Your total: ${playerTotal}. Hit or Stand?`;
    }
  }
  
  function stand() {
    if (gameOver) return;
    
    // Dealer plays
    while (calculateHand(dealerHand) < 17 + difficulty) {
      dealerHand.push(deck.pop()!);
    }
    
    const playerTotal = calculateHand(playerHand);
    const dealerTotal = calculateHand(dealerHand);
    
    if (dealerTotal > 21) {
      gameMessage = `Dealer busts! You win! (${playerTotal} vs ${dealerTotal})`;
      handleWin();
    } else if (playerTotal > dealerTotal) {
      gameMessage = `You win! (${playerTotal} vs ${dealerTotal})`;
      handleWin();
    } else if (playerTotal === dealerTotal) {
      gameMessage = `Push! Tie game. (${playerTotal})`;
      handleTie();
    } else {
      gameMessage = `Dealer wins. (${playerTotal} vs ${dealerTotal})`;
      handleLoss();
    }
    
    gameOver = true;
  }
  
  function handleWin(blackjack = false) {
    const points = blackjack ? 300 : 200 * difficulty;
    gameMessage = `${gameMessage} +${points} points!`;
    dispatch('win', points);
    difficulty = Math.min(difficulty + 1, 5);
    gameOver = true;
  }
  
  function handleLoss() {
    dispatchLoss();
    gameOver = true;
  }
  
  function handleTie() {
    gameOver = true;
  }
  
  function resetGame() {
    playerHand = [];
    dealerHand = [];
    gameMessage = 'New game! Deal to start.';
    gameOver = false;
  }
</script>

<div class="blackjack-game">
  <div class="game-header">
    <button class="back-btn" onclick={onBack}>← Back</button>
    <h2>🃏 Blackjack</h2>
    <div class="player-info">{playerName} vs Dealer</div>
  </div>
  
  <div class="game-message">{gameMessage}</div>
  
  <div class="hands-section">
    <div class="hand dealer-hand">
      <h3>🤖 Dealer</h3>
      <div class="cards">
        {#each dealerHand as card}
          <div class="card">{card}</div>
        {/each}
        {#if dealerHand.length === 0}
          <div class="card empty">?</div>
        {/if}
      </div>
      <div class="hand-total">Total: {calculateHand(dealerHand)}</div>
    </div>
    
    <div class="hand player-hand">
      <h3>👤 You</h3>
      <div class="cards">
        {#each playerHand as card}
          <div class="card">{card}</div>
        {/each}
      </div>
      <div class="hand-total">Total: {calculateHand(playerHand)}</div>
    </div>
  </div>
  
  <div class="controls-section">
    {#if !gameOver && playerHand.length === 0}
      <button class="action-btn deal-btn" onclick={deal}>🎴 Deal</button>
    {:else if !gameOver}
      <button class="action-btn hit-btn" onclick={hit}>📥 Hit</button>
      <button class="action-btn stand-btn" onclick={stand}>✋ Stand</button>
    {:else}
      <button class="action-btn new-btn" onclick={resetGame}>🔄 New Game</button>
    {/if}
  </div>
  
  <div class="difficulty-indicator">
    <span>Difficulty:</span>
    <div class="level-dots">
      {#each Array(5) as _, i}
        <span class="dot {i < difficulty ? 'active' : ''}"></span>
      {/each}
    </div>
  </div>
</div>

<style>
  .blackjack-game {
    max-width: 600px;
    margin: 0 auto;
    padding: 20px;
  }
  
  .game-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    flex-wrap: wrap;
    gap: 15px;
  }
  
  .back-btn, .action-btn {
    background: linear-gradient(135deg, #2ecc71, #27ae60);
    color: white;
    border: none;
    padding: 10px 20px;
    border-radius: 10px;
    cursor: pointer;
    font-weight: 600;
    transition: all 0.3s ease;
  }
  
  h2 {
    color: #2d5016;
    font-size: 1.5rem;
  }
  
  .player-info {
    color: #5a7a5a;
    font-weight: 600;
  }
  
  .game-message {
    text-align: center;
    padding: 15px;
    background: linear-gradient(135deg, #f0fff4, #d4edda);
    border-radius: 15px;
    margin-bottom: 20px;
    font-weight: 600;
    color: #2d5016;
    min-height: 60px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  .hands-section {
    display: grid;
    gap: 20px;
    margin-bottom: 30px;
  }
  
  .hand {
    background: white;
    border-radius: 15px;
    padding: 20px;
    box-shadow: 0 5px 20px rgba(46, 204, 113, 0.15);
  }
  
  .hand h3 {
    margin-bottom: 15px;
    color: #2d5016;
    font-size: 1.2rem;
  }
  
  .cards {
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
    min-height: 80px;
  }
  
  .card {
    background: white;
    border: 2px solid #2ecc71;
    border-radius: 8px;
    padding: 15px 10px;
    min-width: 50px;
    text-align: center;
    font-size: 1.5rem;
    font-weight: bold;
    box-shadow: 0 3px 10px rgba(0,0,0,0.1);
  }
  
  .card.empty {
    background: #e0e0e0;
    border-color: #ccc;
    color: #999;
  }
  
  .hand-total {
    margin-top: 15px;
    font-weight: 700;
    color: #2ecc71;
    font-size: 1.2rem;
  }
  
  .controls-section {
    display: flex;
    justify-content: center;
    gap: 15px;
    flex-wrap: wrap;
  }
  
  .deal-btn {
    background: linear-gradient(135deg, #2ecc71, #27ae60);
    padding: 15px 40px;
    font-size: 1.2rem;
  }
  
  .hit-btn {
    background: linear-gradient(135deg, #3498db, #2980b9);
  }
  
  .stand-btn {
    background: linear-gradient(135deg, #e74c3c, #c0392b);
  }
  
  .new-btn {
    background: linear-gradient(135deg, #f39c12, #e67e22);
    padding: 15px 40px;
    font-size: 1.2rem;
  }
  
  .difficulty-indicator {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    margin-top: 20px;
    color: #2d5016;
  }
  
  .level-dots {
    display: flex;
    gap: 5px;
  }
  
  .dot {
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: #e0e0e0;
  }
  
  .dot.active {
    background: #2ecc71;
    box-shadow: 0 0 10px #2ecc71;
  }
</style>
